import React, { useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TradeDialog from './TradeDialog';

function parseExpiration(exp) {
  if (!exp) return null;
  const upper = String(exp).toUpperCase();
  if (upper === '0DTE') return new Date().toISOString().split('T')[0];
  if (/^\d+DTE$/.test(upper)) {
    const days = parseInt(upper);
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(upper)) return upper;
  const m = upper.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (m) {
    const month = parseInt(m[1]);
    const day = parseInt(m[2]);
    let year = m[3] ? parseInt(m[3]) : null;
    if (!year) {
      const now = new Date();
      const thisYear = now.getFullYear();
      const todayMidnight = new Date(thisYear, now.getMonth(), now.getDate());
      const testDate = new Date(thisYear, month - 1, day);
      year = testDate >= todayMidnight ? thisYear : thisYear + 1;
    } else if (year < 100) {
      year += 2000;
    }
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

function buildManualAlert(searchParams) {
  const assetType = searchParams.get('assetType') || 'OPTION';
  const underlying = searchParams.get('underlying');
  if (!underlying) return null;

  const quantity = parseInt(searchParams.get('quantity')) || 1;
  const priceRaw = searchParams.get('price');
  const price = priceRaw ? parseFloat(priceRaw) : null;
  const orderType = (searchParams.get('orderType') || 'LIMIT').toUpperCase();
  const duration = searchParams.get('duration') || 'DAY';
  const tradeAccountId = searchParams.get('tradeAccountId') || '';
  const rawInstruction = searchParams.get('instruction') || '';
  const alertId = searchParams.get('alertId') || '';

  const optionInstructions = new Set(['BUY_TO_OPEN', 'SELL_TO_OPEN', 'BUY_TO_CLOSE', 'SELL_TO_CLOSE', 'BTO', 'STO', 'BTC', 'STC', 'BOUGHT', 'SOLD', 'OPENED', 'CLOSED', 'SHORT', 'COVERED', 'BUY', 'SELL']);
  const equityInstructions = new Set(['BUY', 'SELL', 'BUY_TO_COVER', 'SELL_SHORT', 'BOUGHT', 'SOLD', 'OPENED', 'CLOSED', 'SHORT', 'COVERED']);

  const instruction = assetType === 'EQUITY'
    ? (equityInstructions.has(rawInstruction.toUpperCase()) ? rawInstruction.toUpperCase() : 'BUY')
    : (optionInstructions.has(rawInstruction.toUpperCase()) ? rawInstruction.toUpperCase() : 'BUY_TO_OPEN');

  if (assetType === 'EQUITY') {
    return {
      isTradeAlert: true,
      assetType: 'EQUITY',
      symbol: underlying.toUpperCase(),
      action: instruction,
      instruction,
      quantity,
      price,
      orderType,
      duration,
      tradeAccountId,
      alertId,
    };
  }

  const strike = parseFloat(searchParams.get('strike')) || 0;
  const putCall = searchParams.get('putCall') || '';
  const expirationRaw = searchParams.get('expiration') || '';
  const expiration = parseExpiration(expirationRaw);

  const legs = [{
    strike,
    side: putCall.toUpperCase(),
    putCall: putCall.toUpperCase(),
    expiration,
    instruction,
    quantity,
  }];

  return {
    isTradeAlert: true,
    assetType: 'OPTION',
    symbol: underlying.toUpperCase(),
    action: instruction,
    instruction,
    quantity,
    price,
    orderType,
    duration,
    tradeAccountId,
    alertId,
    strike,
    putCall: putCall.toUpperCase(),
    expiration: expiration || expirationRaw,
    legs,
    strikes: [strike],
    strategy: 'Custom',
    strategyType: 'custom',
  };
}

export default function QuickTradeDrawer({ open, onClose }) {
  const [searchParams] = useSearchParams();
  const [alert, setAlert] = useState(null);
  const alertAppliedRef = useRef(false);

  const manualAlert = useMemo(() => buildManualAlert(searchParams), [searchParams]);

  React.useEffect(() => {
    if (!open) return;
    if (manualAlert) {
      setAlert(manualAlert);
      alertAppliedRef.current = true;
      return;
    }
    const alertId = searchParams.get('alertId');
    if (!alertId) {
      if (!alertAppliedRef.current) {
        setAlert(null);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/telegram/alert/${alertId}`);
        const data = await resp.json();
        if (cancelled) return;
        if (data.success && data.alert) {
          setAlert(data.alert);
          alertAppliedRef.current = true;
        }
      } catch (err) {
        // silently fail
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, manualAlert, open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 900 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">Quick Trade</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a trade alert or manually enter a strategy to place the order directly.
        </Typography>
        <TradeDialog
          open={true}
          alert={alert}
          onClose={onClose}
          showInputSection={true}
          inline={true}
        />
      </Box>
    </Drawer>
  );
}
